import React, { useState, useCallback } from 'react';
import { Box, Text, Table, Thead, Tbody, Tr, Th, Checkbox } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { DraggableTrackItem, type DraggableTrack } from './DraggableTrackItem';

interface DraggableTrackListProps {
  tracks: DraggableTrack[];
  onReorder: (reorderedTracks: DraggableTrack[]) => void;
  onRemoveTrack: (trackId: string) => void;
  /** ID of the track currently playing in the floating player */
  playingTrackId?: string;
  /** Callback to toggle play in the floating player */
  onPlayToggle?: (track: DraggableTrack) => void;
  /** Edit a single track's metadata. When provided, each row shows an edit button. */
  onEditTrack?: (track: DraggableTrack) => void;
  /** When true, renders a leading checkbox column for bulk selection. */
  selectable?: boolean;
  /** Set of currently-selected track contentIds. */
  selectedIds?: Set<string>;
  /** Toggle a single track's selection. */
  onToggleSelect?: (track: DraggableTrack) => void;
  /** Select or clear every visible track at once. */
  onToggleSelectAll?: (selectAll: boolean) => void;
}

export function DraggableTrackList({ tracks, onReorder, onRemoveTrack, playingTrackId, onPlayToggle, onEditTrack, selectable = false, selectedIds, onToggleSelect, onToggleSelectAll }: DraggableTrackListProps) {
  const { t } = useTranslation();
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    })
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = tracks.findIndex((t) => t.id === active.id);
    const newIndex = tracks.findIndex((t) => t.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(tracks, oldIndex, newIndex).map((track, i) => ({
      ...track,
      position: i + 1,
    }));

    onReorder(reordered);
  }, [tracks, onReorder]);

  const handleDragCancel = useCallback(() => {
    setActiveId(null);
  }, []);

  const activeTrack = activeId ? tracks.find((t) => t.id === activeId) : null;

  const selectedCount = selectedIds ? tracks.filter((t) => selectedIds.has(t.contentId)).length : 0;
  const allSelected = tracks.length > 0 && selectedCount === tracks.length;
  const someSelected = selectedCount > 0 && !allSelected;

  if (tracks.length === 0) {
    return (
      <Box p={8} textAlign="center" data-testid="draggable-track-list-empty">
        <Text color="gray.500">{t('admin.playlists.noTracks', 'No tracks added yet')}</Text>
      </Box>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext items={tracks.map((t) => t.id)} strategy={verticalListSortingStrategy}>
        <Table variant="simple" size="md" data-testid="draggable-track-list">
          <Thead>
            <Tr>
              {selectable && (
                <Th w="40px" px={2}>
                  <Checkbox
                    isChecked={allSelected}
                    isIndeterminate={someSelected}
                    onChange={(e) => onToggleSelectAll?.(e.target.checked)}
                    data-testid="select-all-tracks"
                    aria-label={t('admin.playlists.manage.selectAll', 'Select all')}
                  />
                </Th>
              )}
              <Th w="40px" px={2}>#</Th>
              <Th>{t('admin.library.columns.track', 'Track')}</Th>
              <Th>{t('admin.library.columns.genre', 'Genre')}</Th>
              <Th isNumeric>{t('admin.library.columns.duration', 'Duration')}</Th>
              <Th w="110px" px={2}></Th>
            </Tr>
          </Thead>
          <Tbody>
            {tracks.map((track) => (
              <DraggableTrackItem
                key={track.id}
                track={track}
                onRemove={onRemoveTrack}
                playingTrackId={playingTrackId}
                onPlayToggle={onPlayToggle}
                onEdit={onEditTrack}
                selectable={selectable}
                isSelected={selectedIds?.has(track.contentId) ?? false}
                onToggleSelect={onToggleSelect}
              />
            ))}
          </Tbody>
        </Table>
      </SortableContext>

      <DragOverlay>
        {activeTrack ? (
          <Table variant="simple" size="md">
            <Tbody>
              <DraggableTrackItem
                track={activeTrack}
                onRemove={() => {}}
                isOverlay
                selectable={selectable}
              />
            </Tbody>
          </Table>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
